import { Button } from "@/components/ui/button";
import { ArrowRight, Play, CheckCircle2 } from "lucide-react";

const KINESERVE_HERO_LOGO = "https://customer-assets.emergentagent.com/job_kineserve-app/artifacts/gkt81gsk_KineServe_Logo_Hero.svg";
const GBT_LOGO = "https://greybulltechnologies.com/wp-content/uploads/2022/08/logo.png";

export default function HeroSection({ onDemoClick, onTrialClick }) {
  const highlights = [
    "Deploy in weeks, not months",
    "99.9% Uptime SLA",
    "Full ITIL v4 Compliance",
  ];

  return (
    <section 
      className="pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="container-main">
        {/* Grey Bull Technologies Presents Banner */}
        <div className="flex items-center justify-center gap-3 mb-10 pb-6 border-b border-slate-200">
          <a 
            href="https://greybulltechnologies.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 group"
          >
            <img 
              src={GBT_LOGO} 
              alt="Grey Bull Technologies" 
              className="h-10 md:h-12 object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-slate-600 text-sm md:text-base font-medium">
              proudly presents
            </span>
            <span className="text-slate-900 text-lg md:text-xl font-bold tracking-tight">
              KineServe
            </span>
          </a>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-slide-in-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 mb-6">
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-blue-700">AI-Assisted Process Automation</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-slate-900 mb-6">
              IT Service Management,{" "}
              <span className="text-blue-600">Streamlined</span> for Speed.
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              The intelligent ITSM with process frameworks woven into every workflow. Deploy in days, not months. 
              Reduce costs by 40% with intelligent automation.
            </p>

            {/* Highlights */}
            <div className="flex flex-wrap gap-4 mb-8">
              {highlights.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-slate-700"
                >
                  <CheckCircle2 size={18} className="text-green-600" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={onDemoClick}
                className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-6 text-base font-medium btn-lift"
                data-testid="hero-demo-btn"
              >
                Request a Demo
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button 
                onClick={onTrialClick}
                variant="outline"
                className="border-slate-300 text-slate-900 hover:bg-slate-50 px-8 py-6 text-base font-medium"
                data-testid="hero-trial-btn"
              >
                Start Free Trial
              </Button>
            </div>

            {/* Trust indicator */}
            <p className="mt-8 text-sm text-slate-500">
              Your first month with up to 9 support agents is on us — no strings attached.
            </p>
          </div>

          {/* Right Visual */}
          <div className="relative animate-slide-in-right">
            {/* Background glow */}
            <div className="absolute inset-0 gradient-glow"></div>
            
            {/* Main visual container */}
            <div className="relative bg-white border border-slate-200 shadow-2xl shadow-slate-200/50 p-4 md:p-6">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-slate-100 rounded px-3 py-1.5 text-xs text-slate-500 font-mono">
                    your-company.kineserve.com
                  </div>
                </div>
              </div>

              {/* Dashboard mockup */}
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <img src={KINESERVE_HERO_LOGO} alt="KineServe" className="h-8" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200"></div>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Open Tickets", value: "24", trend: "-12%" },
                    { label: "SLA Compliance", value: "98.5%", trend: "+2.1%" },
                    { label: "Avg Resolution", value: "2.4h", trend: "-18%" },
                  ].map((stat, i) => (
                    <div key={i} className="bg-slate-50 p-3">
                      <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                      <div className={`text-xs font-medium ${stat.trend.startsWith('-') ? 'text-green-600' : 'text-green-600'}`}>
                        {stat.trend}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent tickets */}
                <div className="bg-slate-50 p-3">
                  <div className="text-xs font-medium text-slate-900 mb-2">Recent Incidents</div>
                  {[
                    { id: "INC-2451", title: "Email server latency", status: "In Progress", priority: "High" },
                    { id: "INC-2450", title: "VPN connectivity issue", status: "Resolved", priority: "Medium" },
                  ].map((ticket, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <span className="text-xs font-mono text-slate-500">{ticket.id}</span>
                        <span className="text-xs text-slate-700 ml-2">{ticket.title}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 ${
                        ticket.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {ticket.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -right-4 top-1/4 bg-white border border-slate-200 shadow-lg p-3 animate-fade-in stagger-3 hidden lg:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 flex items-center justify-center">
                  <CheckCircle2 size={16} className="text-green-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-900">Auto-assigned</div>
                  <div className="text-xs text-slate-500">Via AI routing</div>
                </div>
              </div>
            </div>

            <div className="absolute -left-4 bottom-1/4 bg-slate-900 text-white shadow-lg p-3 animate-fade-in stagger-4 hidden lg:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
                  <Play size={16} />
                </div>
                <div>
                  <div className="text-xs font-medium">Amie AI</div>
                  <div className="text-xs text-slate-400">24/7 Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
